import React, { useEffect, useState, useMemo, useRef } from "react";
import { uniq } from "lodash";
import {
    Box,
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Grid,
    Divider,
    DialogActions,
    Button,
    Paper,
    DialogContentText,
    Typography,
    CircularProgress
} from "@mui/material/";
import CloseIcon from "@mui/icons-material/Close";
import { enums, helpers } from "../../../../../common/lib";
import { strings } from "../../../../../common/i18n";
import { useDispatch, useSelector } from "react-redux";
import { format } from "../../../../../common/lib";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import NoAccountsOutlinedIcon from "@mui/icons-material/NoAccountsOutlined";
import Excluded from "../View/Excluded";
import styles from "../styles";
import AssetProgresBar from "../../../../../common/components/AssetProgresBar";
import DonutChart from "../../../../../common/components/DonutChart";
import { logToSentry } from "../../../../../config/Sentry";
import actions from "../../../../../Store/actions";
import useViewAuditHelper from "../useViewAuditHelper";

export default ({ closeDialog, setCloseDialog }) => {
    const { auditsList, dealerContacts } = useSelector((state) => state);
    const [loading, setLoading] = useState(false);
    const submitting = useRef(false);
    const dispatch = useDispatch();

    const { auditorAssetStatus, summaryData, donutValues, data } =
        useViewAuditHelper();

    const { auditAssignments } = useSelector((state) => state);
    const countStartDate = useMemo(() => {
        const assignments = auditAssignments?.data || [];

        const validDates = assignments
            .map((item) => item.countStartDate)
            .filter(Boolean)
            .sort((a, b) => new Date(a) - new Date(b));

        return validDates[0] || null;
        }, [auditAssignments?.data]);

    const exclusionCount = useMemo(() => {
        let count = 0;
        data &&
            data.map((item) => {
                if (item.auditorExcluded || item.controllerExcluded) count++;
            });
        return count;
    }, [data]);

    /** On close of audit dialog. Ignored while a close is in flight so the
     * dialog can't be dismissed out from under an unfinished request. */
    const onCloseDialog = () => {
        if (submitting.current) return;
        setCloseDialog(false);
    };
    /** On close of audit */
    const onCloseAudit = async () => {
        if (submitting.current) return;
        submitting.current = true;
        setLoading(true);
        try {
            await dispatch(
                actions.audits.create.closeRequest(auditsList.item.id)
            );
            dispatch(
                actions.toast.create.success(strings("messages.auditClosed"))
            );
            submitting.current = false;
            setLoading(false);
            onCloseDialog();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On close audit:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onCloseAudit"))
            );
            submitting.current = false;
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                fullWidth={true}
                maxWidth={"lg"}
                onClose={onCloseDialog}
                aria-labelledby="customized-dialog-title"
                open={closeDialog}
            >
                <DialogTitle>{strings("closeAudit")}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={onCloseDialog}
                    disabled={loading}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <DialogContentText sx={{ my: 2 }}>
                        {strings("messages.auditClose")}
                    </DialogContentText>

                    <Grid container spacing={1}>
                        <Grid item xs={exclusionCount > 0 ? 6 : 12}>
                            <Paper elevation={3} sx={{ p: 2 }}>
                                <Typography variant="body1">
                                    {strings("auditDetail")}
                                </Typography>
                                <Divider sx={styles.spacer} />
                                <Box sx={styles.flexContainer}>
                                    <Typography variant="body2" component="p">
                                        {strings("createdDate")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {format.dateTimeNew(
                                            auditsList.item.createdDate
                                        )}
                                    </Typography>
                                </Box>
                                <Box sx={styles.flexContainer}>
                                    <Typography variant="body2" component="p">
                                        {strings("startDate")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {format.dateTimeNew(
                                            auditsList.item.startDate
                                        )}
                                    </Typography>
                                </Box>
                                <Box sx={styles.flexContainer}>
                                    <Typography variant="body2" component="p">
                                        {strings("countStartDate")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {countStartDate
                                            ? format.dateTimeNew(countStartDate)
                                            : strings("na")}
                                    </Typography>
                                </Box>
                                <Box sx={styles.flexContainer}>
                                    <Typography variant="body2" component="p">
                                        {strings("submittedDate")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {format.dateTimeNew(
                                            auditsList.item.latestSubmissionDate
                                        )}
                                    </Typography>
                                </Box>
                                <Box sx={styles.flexContainer}>
                                    <Typography variant="body2" component="p">
                                        {strings("controller")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {summaryData.controllers}
                                    </Typography>
                                </Box>

                                <Divider sx={styles.spacer} />
                                <Typography variant="body1">
                                    {strings("auditors")}
                                </Typography>
                                {auditorAssetStatus &&
                                    auditorAssetStatus.map((item, index) => (
                                        <Box
                                            key={`auditor-${index}`}
                                            sx={styles.flexContainer}
                                        >
                                            <Typography
                                                sx={styles.label}
                                                variant="body2"
                                            >
                                                {item.auditor?.displayName
                                                    .length > 0 ? (
                                                    item.auditor?.displayName
                                                ) : (
                                                    <NoAccountsOutlinedIcon
                                                        title="noUser"
                                                        color="primary"
                                                    />
                                                )}
                                            </Typography>
                                            <AssetProgresBar data={item} />
                                        </Box>
                                    ))}

                                <Divider sx={styles.spacer} />
                                <Typography variant="body1">
                                    {strings("dealer")}
                                </Typography>
                                {dealerContacts &&
                                    dealerContacts.map((item, index) => (
                                        <Box
                                            sx={styles.flexContainer}
                                            key={item.signatureDate}
                                        >
                                            <Typography
                                                variant="body2"
                                                component="p"
                                            >
                                                {item.name}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`${strings(
                                                    "signedOn"
                                                )} ${format.dateTimeNew(
                                                    item.signatureDate
                                                )}`}
                                            </Typography>
                                        </Box>
                                    ))}
                                <Divider sx={styles.spacer} />
                                <Typography variant="body1">
                                    {strings("assetDetail")}
                                </Typography>
                                <Divider sx={styles.spacer} />
                                <DonutChart data={donutValues} />
                            </Paper>
                        </Grid>
                        {exclusionCount > 0 && (
                            <Grid item xs={6} md={6}>
                                <Paper elevation={3} sx={{ p: 2 }}>
                                    <Excluded value={data} />
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ m: 2 }}>
                    <Button
                        sx={styles.secondaryButton}
                        variant="contained"
                        onClick={onCloseDialog}
                        disabled={loading}
                    >
                        {strings("cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        sx={styles.primaryButton}
                        onClick={onCloseAudit}
                        disabled={loading}
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : null
                        }
                    >
                        {strings("closeAudit")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
